import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  GraduationCap, 
  BookOpen, 
  CheckCircle, 
  Clock, 
  Target,
  Award,
  Users,
  BarChart3
} from "lucide-react";
import NavBar from "@/components/NavBar";

export default function TrainingHub() {
  const [selectedCase, setSelectedCase] = useState("soc");

  const trainingModules = {
    soc: {
      title: "Strategic Outline Case (SOC)",
      modules: [
        { case: "Strategic Case", modules: 3, completed: 2 },
        { case: "Economic Case", modules: 4, completed: 1 },
        { case: "Commercial Case", modules: 4, completed: 0 },
        { case: "Financial Case", modules: 3, completed: 0 },
        { case: "Management Case", modules: 3, completed: 0 }
      ]
    },
    obc: {
      title: "Outline Business Case (OBC)",
      modules: [
        { case: "Strategic Case", modules: 3, completed: 0 },
        { case: "Economic Case", modules: 4, completed: 0 },
        { case: "Commercial Case", modules: 4, completed: 0 },
        { case: "Financial Case", modules: 3, completed: 0 },
        { case: "Management Case", modules: 3, completed: 0 }
      ]
    },
    fbc: {
      title: "Full Business Case (FBC)",
      modules: [
        { case: "Strategic Case", modules: 3, completed: 0 },
        { case: "Economic Case", modules: 4, completed: 0 },
        { case: "Commercial Case", modules: 4, completed: 0 },
        { case: "Financial Case", modules: 3, completed: 0 },
        { case: "Management Case", modules: 3, completed: 0 }
      ]
    }
  };

  const userStats = {
    totalModulesCompleted: 3,
    totalModulesAvailable: 51,
    currentStreak: 5,
    certificatesEarned: 0
  };

  const calculateProgress = (completed: number, total: number) => {
    return (completed / total) * 100;
  };

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Training Hub</h1>
            <p className="text-muted-foreground">
              Structured learning modules for HM Treasury business case development and Green Book compliance
            </p>
          </div>

          {/* User Progress Overview */}
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="text-2xl font-bold">{userStats.totalModulesCompleted}</div>
                    <div className="text-sm text-muted-foreground">Modules Completed</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-green-600" />
                  <div>
                    <div className="text-2xl font-bold">
                      {Math.round(calculateProgress(userStats.totalModulesCompleted, userStats.totalModulesAvailable))}%
                    </div>
                    <div className="text-sm text-muted-foreground">Overall Progress</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-orange-600" />
                  <div>
                    <div className="text-2xl font-bold">{userStats.currentStreak}</div>
                    <div className="text-sm text-muted-foreground">Day Streak</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-purple-600" />
                  <div>
                    <div className="text-2xl font-bold">{userStats.certificatesEarned}</div>
                    <div className="text-sm text-muted-foreground">Certificates</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Training Requirements */}
          <Card className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-6 w-6" />
                Training Requirements
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-background rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Module Pass Rate</h4>
                  <div className="text-2xl font-bold text-green-600">75%</div>
                  <p className="text-sm text-muted-foreground">Minimum score required</p>
                </div>
                <div className="bg-background rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Questions per Module</h4>
                  <div className="text-2xl font-bold text-blue-600">20+</div>
                  <p className="text-sm text-muted-foreground">Multiple choice format</p>
                </div>
                <div className="bg-background rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Final Assessment</h4>
                  <div className="text-2xl font-bold text-purple-600">30</div>
                  <p className="text-sm text-muted-foreground">Comprehensive questions</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Training Modules by Business Case Type */}
          <Tabs value={selectedCase} onValueChange={setSelectedCase}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="soc">Strategic Outline Case</TabsTrigger>
              <TabsTrigger value="obc">Outline Business Case</TabsTrigger>
              <TabsTrigger value="fbc">Full Business Case</TabsTrigger>
            </TabsList>

            {Object.entries(trainingModules).map(([key, data]) => (
              <TabsContent key={key} value={key} className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>{data.title} Training Modules</CardTitle>
                    <CardDescription>
                      Complete all modules to unlock the comprehensive assessment
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {data.modules.map((module, index) => (
                        <div key={index} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="font-semibold">{module.case}</h4>
                              <p className="text-sm text-muted-foreground">
                                {module.modules} modules • {module.modules * 20}+ questions
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={module.completed > 0 ? "default" : "secondary"}>
                                {module.completed}/{module.modules} completed
                              </Badge>
                              {module.completed === module.modules && (
                                <CheckCircle className="h-5 w-5 text-green-600" />
                              )}
                            </div>
                          </div>
                          <div className="mb-3">
                            <Progress 
                              value={calculateProgress(module.completed, module.modules)} 
                              className="h-2"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant={module.completed > 0 ? "outline" : "default"}
                            >
                              {module.completed > 0 ? "Continue" : "Start"} Training
                            </Button>
                            {module.completed === module.modules && (
                              <Button size="sm" variant="outline">
                                Take Assessment
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Comprehensive Assessment */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Comprehensive {data.title} Assessment
                    </CardTitle>
                    <CardDescription>
                      30 questions covering all aspects of the {data.title.toLowerCase()}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">
                          Complete all modules above to unlock this assessment
                        </p>
                        <div className="flex items-center gap-4 text-sm">
                          <span>• 30 multiple choice questions</span>
                          <span>• 75% pass rate required</span>
                          <span>• Certificate upon completion</span>
                        </div>
                      </div>
                      <Button disabled>
                        Take Assessment
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );
}